import { AxiosProgressEvent } from "axios";
import { ApiResponse } from "../context/VolunteersHighlightContext/constants";
import {
	UpdateVolunteersGalleryItemRequest,
	UpdateVolunteersGalleryRequest,
	VolunteersGalleryContextType,
	VolunteersGalleryGetApiResponse,
	VolunteersGalleryListApiResponse,
} from "../context/VolunteersGalleryContext/constants";
import { axiosInstance } from "../utils/axiosInstance";

const ENDPOINTS = {
	VOLUNTEERS_GALLERY: "/cms/volunteers-gallery",
	ITEMS: "/cms/volunteers-gallery/items",
};

export interface NewVolunteersGallery {
	contextType: VolunteersGalleryContextType;
	contextId: string | null;
	isActive: boolean;
}

export type UploadProgressHandler = (loadedBytes: number) => void;

const toAxiosProgress = (onProgress?: UploadProgressHandler) =>
	onProgress ? { onUploadProgress: (event: AxiosProgressEvent) => onProgress(event.loaded) } : undefined;

// The same key repeated sends several files; their order sets the display order of the new items.
const appendImages = (formData: FormData, files: File[]) => {
	files.forEach(file => formData.append("Images", file, file.name));
};

// A home page gallery must not send a project.
export const buildAddVolunteersGalleryFormData = (gallery: NewVolunteersGallery, files: File[]): FormData => {
	const formData = new FormData();
	formData.append("ContextType", String(gallery.contextType));
	if (gallery.contextType === VolunteersGalleryContextType.Event && gallery.contextId) {
		formData.append("ContextId", gallery.contextId);
	}
	formData.append("IsActive", String(gallery.isActive));
	appendImages(formData, files);
	return formData;
};

// New items are added after the last one and are active (the backend's default).
export const buildAddVolunteersGalleryItemsFormData = (files: File[]): FormData => {
	const formData = new FormData();
	appendImages(formData, files);
	return formData;
};

// Only the given fields are sent; the backend keeps the others.
export const buildUpdateVolunteersGalleryItemFormData = (request: UpdateVolunteersGalleryItemRequest): FormData => {
	const formData = new FormData();
	if (request.image) {
		formData.append("Image", request.image, request.image.name);
	}
	if (request.displayOrder !== undefined) {
		formData.append("DisplayOrder", String(request.displayOrder));
	}
	if (request.isActive !== undefined) {
		formData.append("IsActive", String(request.isActive));
	}
	return formData;
};

const NO_CONTENT: ApiResponse<null> = { result: null, error: null, isSuccessful: true };

export class VolunteersGalleryService {
	static async getVolunteersGalleryList(): Promise<VolunteersGalleryListApiResponse> {
		const response = await axiosInstance.get(ENDPOINTS.VOLUNTEERS_GALLERY);
		return response.data;
	}

	static async getVolunteersGalleryDetails(galleryId: string): Promise<VolunteersGalleryGetApiResponse> {
		const response = await axiosInstance.get(`${ENDPOINTS.VOLUNTEERS_GALLERY}/${galleryId}`);
		return response.data;
	}

	// Answers 201 without the new gallery's id.
	static async addVolunteersGallery(gallery: NewVolunteersGallery, files: File[], onProgress?: UploadProgressHandler): Promise<ApiResponse<unknown>> {
		const response = await axiosInstance.post(ENDPOINTS.VOLUNTEERS_GALLERY, buildAddVolunteersGalleryFormData(gallery, files), toAxiosProgress(onProgress));
		return response.data;
	}

	static async updateVolunteersGallery(request: UpdateVolunteersGalleryRequest): Promise<ApiResponse<unknown>> {
		const response = await axiosInstance.patch(ENDPOINTS.VOLUNTEERS_GALLERY, request);
		return response.data;
	}

	static async removeVolunteersGallery(galleryId: string): Promise<ApiResponse<unknown>> {
		const response = await axiosInstance.delete(`${ENDPOINTS.VOLUNTEERS_GALLERY}/${galleryId}`);
		return response.status === 204 ? NO_CONTENT : response.data;
	}

	static async addVolunteersGalleryItems(galleryId: string, files: File[], onProgress?: UploadProgressHandler): Promise<ApiResponse<unknown>> {
		const response = await axiosInstance.post(
			`${ENDPOINTS.VOLUNTEERS_GALLERY}/${galleryId}/items`,
			buildAddVolunteersGalleryItemsFormData(files),
			toAxiosProgress(onProgress)
		);
		return response.data;
	}

	static async updateVolunteersGalleryItem(request: UpdateVolunteersGalleryItemRequest): Promise<ApiResponse<unknown>> {
		const response = await axiosInstance.patch(`${ENDPOINTS.ITEMS}/${request.itemId}`, buildUpdateVolunteersGalleryItemFormData(request));
		return response.data;
	}

	// Answers 204 without a body.
	static async removeVolunteersGalleryItem(itemId: string): Promise<ApiResponse<unknown>> {
		const response = await axiosInstance.delete(`${ENDPOINTS.ITEMS}/${itemId}`);
		return response.status === 204 ? NO_CONTENT : response.data;
	}
}
